/**
 * Frontend-only catalog of consent categories — the purposes a visitor decides
 * about. The concrete technologies live in `consent-catalog.constants.ts` and
 * reference these keys; no Sanity configuration is involved.
 *
 * Keys only: the visible label and description are UI chrome and live in the
 * i18n dictionary (`lib/i18n/dictionaries/*.ts`, `consent.categories`), keyed by
 * the same `key`. Keeping the key here and the copy there is what lets the
 * banner speak the visitor's language while a stored decision stays comparable
 * across languages.
 */
export const CONSENT_CATEGORIES = [
    { key: 'functional', required: true },
    { key: 'analytics', required: false },
    { key: 'marketing', required: false },
    { key: 'external-media', required: false },
] as const;

/**
 * localStorage key + version. Bump the version to invalidate stored consent —
 * only for structural changes to the payload. A new service does NOT need a
 * bump: it is detected by id and re-prompted without discarding the existing
 * decision (see `findUndecidedServices`).
 */
export const CONSENT_STORAGE_KEY = 'od-consent';
export const CONSENT_STORAGE_VERSION = 2;
/** An accepting decision is re-requested after this period (in days). */
export const CONSENT_TTL_DAYS = 180;
/** A rejecting decision is re-requested the next day. */
export const CONSENT_REJECTED_TTL_DAYS = 1;

/**
 * Mirror of "a decision exists" as a cookie. localStorage is invisible to the
 * server, which forced the banner to wait for hydration — and made its text the
 * LCP element with over a second of render delay. With the cookie the server
 * knows whether to render the banner at all, so it paints with the first frame
 * and never flashes for visitors who already decided.
 *
 * Split by outcome because the two lifetimes differ: an acceptance keeps the
 * banner away for CONSENT_TTL_DAYS, a rejection only for
 * CONSENT_REJECTED_TTL_DAYS. At most one of the two keys is ever set.
 *
 * They store the consent decision itself and are therefore technically necessary.
 */
export const CONSENT_COOKIE_ACCEPTED_KEY = 'od-consent-accepted';
export const CONSENT_COOKIE_REJECTED_KEY = 'od-consent-rejected';
export const CONSENT_COOKIE_VALUE = String(CONSENT_STORAGE_VERSION);
